export const INCH_SIMULATION_OPTIONS = [20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 46, 48, 50] as const;

export type InchSimulationOption = (typeof INCH_SIMULATION_OPTIONS)[number];

export const DEFAULT_SIMULATED_WIDTH = 1920;
export const RESOLUTION_SLIDER_MIN = 1280;
export const RESOLUTION_SLIDER_MAX = 3840;
export const RESOLUTION_SLIDER_STEP = 8;

export const CM_PER_INCH = 2.54;
export const CUSTOM_SIZE_MIN_CM = 1;
/** Teto só para evitar valor absurdo no input; totem/parede LED cabem (20 m). */
export const CUSTOM_SIZE_MAX_CM = 2000;

export const RESOLUTION_SIMULATION_OPTIONS = [
  { id: '720p', label: '720p', width: 1280, height: 720 },
  { id: 'hd768', label: 'HD 768p', width: 1366, height: 768 },
  { id: '1080p', label: '1080p', width: 1920, height: 1080 },
  { id: '4k', label: '4K', width: 3840, height: 2160 },
] as const;

export type ResolutionSimulationId = (typeof RESOLUTION_SIMULATION_OPTIONS)[number]['id'];

export type CustomSizeCm = {
  widthCm: number;
  heightCm: number;
};

export const DEFAULT_ASPECT_RATIO_ID = '16:9' as const;

export const ASPECT_RATIO_OPTIONS = [
  { id: '16:9', label: '16:9', w: 16, h: 9 },
  { id: '16:10', label: '16:10', w: 16, h: 10 },
  { id: '4:3', label: '4:3', w: 4, h: 3 },
  { id: '21:9', label: '21:9', w: 21, h: 9 },
  { id: '9:16', label: '9:16', w: 9, h: 16 },
] as const;

export type AspectRatioId = (typeof ASPECT_RATIO_OPTIONS)[number]['id'];

export function getAspectRatioOption(id: AspectRatioId) {
  return ASPECT_RATIO_OPTIONS.find((option) => option.id === id) ?? ASPECT_RATIO_OPTIONS[0];
}

export function heightFromWidth16x9(widthPx: number): number {
  return heightFromAspect(widthPx, '16:9');
}

/** Lado maior do slider vira o lado maior do painel (9:16 → 1080×1920 no 1080p). */
export function panelPixelsForAspect(
  sliderWidth: number,
  aspectId: AspectRatioId
): { widthPx: number; heightPx: number } {
  const longEdge = clampSimulatedWidth(sliderWidth);
  const aspect = getAspectRatioOption(aspectId);
  if (aspect.w >= aspect.h) {
    return {
      widthPx: longEdge,
      heightPx: Math.max(1, Math.round((longEdge * aspect.h) / aspect.w)),
    };
  }
  return {
    widthPx: Math.max(1, Math.round((longEdge * aspect.w) / aspect.h)),
    heightPx: longEdge,
  };
}

export function heightFromAspect(widthPx: number, aspectId: AspectRatioId): number {
  return panelPixelsForAspect(widthPx, aspectId).heightPx;
}

export function clampSimulatedWidth(widthPx: number): number {
  if (RESOLUTION_SIMULATION_OPTIONS.some((option) => option.width === widthPx)) {
    return widthPx;
  }
  const snapped = Math.round(widthPx / RESOLUTION_SLIDER_STEP) * RESOLUTION_SLIDER_STEP;
  return Math.min(RESOLUTION_SLIDER_MAX, Math.max(RESOLUTION_SLIDER_MIN, snapped));
}

export function clampCustomCm(value: number): number {
  return Math.min(CUSTOM_SIZE_MAX_CM, Math.max(CUSTOM_SIZE_MIN_CM, value));
}

export function parseCustomCmInput(raw: string): number | null {
  const normalized = raw.replace(',', '.').trim();
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return clampCustomCm(value);
}

export function completeCustomSize(
  widthCm: number | null,
  heightCm: number | null
): CustomSizeCm | null {
  if (widthCm == null || heightCm == null || widthCm <= 0 || heightCm <= 0) {
    return null;
  }
  return { widthCm, heightCm };
}

export function isCompleteCustomSize(
  widthCm: number | null,
  heightCm: number | null
): boolean {
  return completeCustomSize(widthCm, heightCm) != null;
}

/** Diagonal física em polegadas a partir da largura × altura em cm. */
export function diagonalInchesFromCm(widthCm: number, heightCm: number): number {
  return Math.hypot(widthCm, heightCm) / CM_PER_INCH;
}

/**
 * Encaixa a resolução do slider no retângulo físico.
 * Totem mais alto que largo → painel em pé (ex.: 1080×1920).
 * O lado maior do slider (1920 no 1080p) vira o lado maior do painel.
 */
export function panelPixelsForPhysicalSize(
  sliderWidth: number,
  widthCm: number,
  heightCm: number
): { widthPx: number; heightPx: number } {
  const longEdge = clampSimulatedWidth(sliderWidth);
  if (widthCm >= heightCm) {
    return {
      widthPx: longEdge,
      heightPx: Math.max(1, Math.round(longEdge * (heightCm / widthCm))),
    };
  }
  return {
    widthPx: Math.max(1, Math.round(longEdge * (widthCm / heightCm))),
    heightPx: longEdge,
  };
}

export type ScreenConfig = {
  inches: number;
  widthPx: number;
  heightPx: number;
  widthCm?: number;
  heightCm?: number;
};

const PREVIEW_INSET = 0.96;
const CSS_REFERENCE_DPI = 96;

export function getHostDevicePixelRatio(): number {
  if (typeof window === 'undefined') return 1;
  return window.devicePixelRatio || 1;
}

export function getMonitorResolution(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 1920, height: 1080 };
  return {
    width: window.screen.width || 1920,
    height: window.screen.height || 1080,
  };
}

export function getMonitorPhysicalResolution(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 1920, height: 1080 };
  const dpr = getHostDevicePixelRatio();
  const css = getMonitorResolution();
  return {
    width: Math.round(css.width * dpr),
    height: Math.round(css.height * dpr),
  };
}

/** Só para o rótulo “monitor atual” no menu. */
export function estimateMonitorDiagonalInches(): number {
  if (typeof window === 'undefined') return 24;
  const physical = getMonitorPhysicalResolution();
  const inches = Math.hypot(physical.width, physical.height) / CSS_REFERENCE_DPI;
  return Math.min(85, Math.max(10, Math.round(inches * 10) / 10));
}

/**
 * Polegada em que esta resolução tem ~96 PPI (1 CSS px ≈ 1 px de referência).
 * 1920×1080 → ~22,9". 3840×2160 → ~45,9".
 */
export function referenceInchesForResolution(widthPx: number, heightPx: number): number {
  return Math.hypot(widthPx, heightPx) / CSS_REFERENCE_DPI;
}

export interface TvSimulationMetrics {
  simulated: ScreenConfig;
  cssWidth: number;
  cssHeight: number;
  /** Só para caber no monitor do usuário. */
  scale: number;
  customMode: boolean;
}

export function fitSimulatedViewport(
  cssWidth: number,
  cssHeight: number,
  containerWidth: number,
  containerHeight: number
): number {
  const fit = Math.min(
    (containerWidth * PREVIEW_INSET) / cssWidth,
    (containerHeight * PREVIEW_INSET) / cssHeight
  );
  return Math.min(1, Math.max(0.04, fit));
}

function metricsFromPanel(params: {
  widthPx: number;
  heightPx: number;
  inches: number;
  containerWidth: number;
  containerHeight: number;
  widthCm?: number;
  heightCm?: number;
}): TvSimulationMetrics {
  const { widthPx, heightPx, inches, containerWidth, containerHeight, widthCm, heightCm } = params;
  const referenceInches = referenceInchesForResolution(widthPx, heightPx);
  const inchRatio = inches > 0 ? referenceInches / inches : 1;
  const cssWidth = Math.max(1, Math.round(widthPx * inchRatio));
  const cssHeight = Math.max(1, Math.round(heightPx * inchRatio));

  return {
    simulated: { inches, widthPx, heightPx, widthCm, heightCm },
    cssWidth,
    cssHeight,
    scale: fitSimulatedViewport(cssWidth, cssHeight, containerWidth, containerHeight),
    customMode: widthCm != null && heightCm != null,
  };
}

/**
 * 1. Mundo = tela desejada (polegada 16:9 OU cm largura×altura + resolução).
 * 2. Depois: só reduzir para caber no monitor de quem está olhando.
 *
 * Polegada e cm são exclusivos: cm completa prevalece.
 * Resolução entra nos dois caminhos (PPI do painel).
 */
export function getTvSimulationMetrics(params: {
  selectedInches: number | null;
  selectedWidthPx: number | null;
  selectedAspectId?: AspectRatioId;
  customWidthCm: number | null;
  customHeightCm: number | null;
  containerWidth: number;
  containerHeight: number;
}): TvSimulationMetrics {
  const {
    selectedInches,
    selectedWidthPx,
    selectedAspectId = DEFAULT_ASPECT_RATIO_ID,
    customWidthCm,
    customHeightCm,
    containerWidth,
    containerHeight,
  } = params;

  const sliderWidth = selectedWidthPx ?? DEFAULT_SIMULATED_WIDTH;
  const customSize = completeCustomSize(customWidthCm, customHeightCm);

  if (customSize) {
    // Cm define o retângulo (aspecto). Resolução define o canvas em px — sem inflar com a
    // diagonal, senão 30×30 vira um viewport enorme e o 100vh da página estica em faixa.
    const panel = panelPixelsForPhysicalSize(sliderWidth, customSize.widthCm, customSize.heightCm);
    const cssWidth = panel.widthPx;
    const cssHeight = panel.heightPx;
    const cssPxPerCm = CSS_REFERENCE_DPI / CM_PER_INCH;
    const physicalScale = Math.min(
      (customSize.widthCm * cssPxPerCm) / cssWidth,
      (customSize.heightCm * cssPxPerCm) / cssHeight
    );
    const fitScale = fitSimulatedViewport(cssWidth, cssHeight, containerWidth, containerHeight);
    const scale = Math.min(1, Math.max(0.04, Math.min(physicalScale, fitScale)));

    return {
      simulated: {
        inches: diagonalInchesFromCm(customSize.widthCm, customSize.heightCm),
        widthPx: panel.widthPx,
        heightPx: panel.heightPx,
        widthCm: customSize.widthCm,
        heightCm: customSize.heightCm,
      },
      cssWidth,
      cssHeight,
      scale,
      customMode: true,
    };
  }

  const panel = panelPixelsForAspect(sliderWidth, selectedAspectId);
  const referenceInches = referenceInchesForResolution(panel.widthPx, panel.heightPx);

  return metricsFromPanel({
    widthPx: panel.widthPx,
    heightPx: panel.heightPx,
    inches: selectedInches ?? referenceInches,
    containerWidth,
    containerHeight,
  });
}
